import { useAppContext } from "@/app/providers/AppContext";
import { useTheme } from "@/app/providers/ThemeContext";
import { FONT_SANS } from "@/shared/lib/typography";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const interFont = FONT_SANS;

const plans = [
    {
        tier: "Trial",
        name: "Free",
        price: "Free",
        priceStyle: { fontSize: "1.5rem", color: "#00B8DB" },
        freq: "14-day trial",
        tagline: "Try everything for free. No card required.",
        features: [
            "All LinkedHash capabilities for 14 days",
            "2 active watches, 5-day max duration",
            "15-minute evaluation intervals",
            "3 alerts per watch",
            "Community support (GitHub)",
        ],
        featureClass: "light",
        dotPattern: "rgba(0,184,219,0.2)",
        cta: "Start Free Trial",
        ctaStyle: { background: "#0d0d0d", color: "white" },
        ctaHover: { background: "#00B8DB" },
        cardBg: "#ffffff",
        cardBorder: "#e5e5e5",
        dark: false,
    },
    {
        tier: "Toolbox",
        name: "Toolbox",
        price: "$18",
        freq: "per month",
        tagline: "Monthly access to LinkedHash inside Claude.",
        features: [
            "Access to all current + future tools",
            "Run any playbooks and workflows on demand",
            "0 active watches, no automated alerts",
            "Upgrade to Oracle anytime",
            "Monthly billing, cancel anytime",
        ],
        featureClass: "light",
        dotPattern: "rgba(0,184,219,0.15)",
        cta: "Get Toolbox",
        ctaStyle: { background: "#0d0d0d", color: "white" },
        ctaHover: { background: "#00B8DB" },
        cardBg: "#ffffff",
        cardBorder: "#e5e5e5",
        dark: false,
    },
    {
        tier: "Oracle",
        name: "Oracle",
        price: "$29",
        freq: "per month",
        tagline: "Add automated watches and alerts.",
        features: [
            "Everything in Toolbox",
            "10 active watches",
            "30-day max watch duration",
            "5-minute evaluation intervals",
            "20 alerts per watch",
            "Email support, typically under 48 hours",
        ],
        featureClass: "dark",
        dotPattern: null,
        cta: "Enable Oracle Alerts",
        ctaStyle: { background: "white", color: "#0d0d0d" },
        ctaHover: { background: "rgba(255,255,255,0.85)" },
        cardBg: "#0d0d0d",
        cardBorder: "rgba(255,255,255,0.05)",
        dark: true,
    },
    {
        tier: "Oracle Monitor",
        name: "Monitor",
        price: "$79",
        freq: "per month",
        tagline: "For desks that need continuous monitoring.",
        features: [
            "Everything in Oracle",
            "50 active watches",
            "30-day max watch duration",
            "5-minute evaluation intervals",
            "20 alerts per watch",
            "Continuous monitoring when offline",
            "Priority email support, under 24 hours",
        ],
        featureClass: "white",
        dotPattern: "rgba(255,255,255,0.18)",
        cta: "Scale Monitoring",
        ctaStyle: { background: "white", color: "#00B8DB", fontWeight: 700 },
        ctaHover: { background: "rgba(255,255,255,0.88)" },
        cardBg: "#00B8DB",
        cardBorder: "rgba(255,255,255,0.08)",
        dark: true,
    },
];

export default function Plans() {
    const navigate = useNavigate();
    const { profile, addToast, isAuthenticated } = useAppContext();
    const { theme } = useTheme();
    const isDark = theme === "dark";
    const sectionRef = useRef(null);
    const badgeRef = useRef(null);
    const h2Ref = useRef(null);
    const pRef = useRef(null);
    const cardsRef = useRef([]);
    const ctaBtnsRef = useRef([]);
    const noteRef = useRef(null);

    // Map plan tier to Stripe plan key
    const getPlanKey = (tier: string): string => {
        const tierMap: Record<string, string> = {
            "Trial": "trial",
            "Toolbox": "toolbox",
            "Oracle": "oracle",
            "Oracle Monitor": "oracle_monitor",
        };
        return tierMap[tier] || tier.toLowerCase();
    };

    const handlePlanClick = useCallback((tier: string) => {
        const planKey = getPlanKey(tier);
        
        if (!isAuthenticated) {
            // User is not logged in
            // Trial is automatically created on signup, so no need to store it
            if (planKey === "trial") {
                navigate("/login?redirect=dashboard");
            } else {
                // Store selected plan for paid tiers and redirect to login
                sessionStorage.setItem("pendingPlan", planKey);
                navigate("/login?redirect=plan");
            }
            return;
        }

        // User is logged in
        const currentPlan = profile?.plan;

        // If user already has this plan, skip
        if (currentPlan === planKey) {
            addToast("You already have this plan", "info");
            return;
        }

        // Navigate to portal account with selected plan
        navigate(`/portal/account?plan=${planKey}`);
    }, [isAuthenticated, profile?.plan, navigate, addToast]);

    useEffect(() => {
        const ctx = gsap.context(() => {

            // ── Header ────────────────────────────────────────────────────────
            gsap.timeline({
                scrollTrigger: { trigger: badgeRef.current, start: "top 84%", toggleActions: "play none none none" },
            })
                .fromTo(badgeRef.current,
                    { opacity: 0, y: 16, scale: 0.93 },
                    { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "power3.out" }
                )
                .fromTo(h2Ref.current,
                    { opacity: 0, y: 22 },
                    { opacity: 1, y: 0, duration: 0.6, ease: "power3.out" },
                    "-=0.28"
                )
                .fromTo(pRef.current,
                    { opacity: 0, y: 14 },
                    { opacity: 1, y: 0, duration: 0.45, ease: "power2.out" },
                    "-=0.3"
                );

            // ── Pricing cards: stagger up with slight Y + scale ───────────────
            gsap.timeline({
                scrollTrigger: { trigger: cardsRef.current[0], start: "top 82%", toggleActions: "play none none none" },
            }).fromTo(
                cardsRef.current.filter(Boolean),
                { opacity: 0, y: 44, scale: 0.96 },
                { opacity: 1, y: 0, scale: 1, duration: 0.6, stagger: 0.1, ease: "power3.out" }
            );

            // ── Price numbers: count-in feel ─────────────────────────────────
            cardsRef.current.filter(Boolean).forEach((card, i) => {
                const priceEl = card.querySelector(".p-price");
                if (!priceEl) return;
                gsap.fromTo(priceEl,
                    { opacity: 0, y: 12, scale: 0.85 },
                    {
                        opacity: 1, y: 0, scale: 1, duration: 0.45, ease: "back.out(1.5)",
                        scrollTrigger: { trigger: card, start: "top 80%", toggleActions: "play none none none" },
                        delay: 0.15 + i * 0.08,
                    }
                );
            });

            // ── Feature list items: micro stagger per card ────────────────────
            cardsRef.current.filter(Boolean).forEach((card) => {
                const items = card.querySelectorAll(".pf-item");
                gsap.fromTo(items,
                    { opacity: 0, x: -10 },
                    {
                        opacity: 1, x: 0, duration: 0.3, stagger: 0.06, ease: "power2.out",
                        scrollTrigger: { trigger: card, start: "top 76%", toggleActions: "play none none none" },
                    }
                );
            });

            // ── Bottom note ───────────────────────────────────────────────────
            gsap.fromTo(noteRef.current,
                { opacity: 0, y: 10 },
                {
                    opacity: 1, y: 0, duration: 0.45, ease: "power2.out",
                    scrollTrigger: { trigger: noteRef.current, start: "top 90%", toggleActions: "play none none none" },
                }
            );

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    const getCardStyles = (plan) => {
        // Oracle Monitor is always premium cyan
        if (plan.tier === "Oracle Monitor") {
            return {
                bg: "#00B8DB",
                border: "rgba(255, 255, 255, 0.08)",
                text: "white", // white text on cyan background looks very premium
                sub: "rgba(255, 255, 255, 0.8)",
                mid: "rgba(255, 255, 255, 0.85)",
                borderB: "rgba(255, 255, 255, 0.2)",
                feat: "rgba(255, 255, 255, 0.9)",
                dark: true,
                dotPattern: "rgba(255, 255, 255, 0.18)",
                ctaBg: "white",
                ctaColor: "#00B8DB"
            };
        }

        // Other tiers are dynamic based on isDark
        if (isDark) {
            return {
                bg: "#111820",
                border: plan.tier === "Oracle" ? "rgba(0, 184, 219, 0.35)" : "rgba(255, 255, 255, 0.08)",
                text: "white",
                sub: "rgba(255, 255, 255, 0.4)",
                mid: "rgba(255, 255, 255, 0.55)",
                borderB: "rgba(255, 255, 255, 0.08)",
                feat: "rgba(255, 255, 255, 0.7)",
                dark: true,
                dotPattern: plan.tier === "Oracle" ? null : "rgba(0, 184, 219, 0.08)",
                ctaBg: "#00b8db",
                ctaColor: "#000000"
            };
        } else {
            return {
                bg: "#ffffff",
                border: plan.tier === "Oracle" ? "rgba(0, 184, 219, 0.3)" : "#c4c2bc",
                text: "#1a1a1a",
                sub: "#999",
                mid: "#666",
                borderB: "#e5e5e5",
                feat: "#555",
                dark: false,
                dotPattern: plan.tier === "Oracle" ? null : "rgba(0, 184, 219, 0.15)",
                ctaBg: "#00B8DB",
                ctaColor: "#000000"
            };
        }
    };

    return (
        <div id="plans" ref={sectionRef} style={{ background: isDark ? "var(--lh-bg)" : "#ffffff", fontFamily: interFont }}>
            <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "72px 24px" }}>

                {/* ── Header ───────────────────────────────────────────────────── */}
                <div style={{ textAlign: "center", marginBottom: "44px" }}>
                    <div
                        ref={badgeRef}
                        className="inline-flex items-center justify-center gap-2 mb-4"
                        style={{
                            fontFamily: interFont, fontSize: "0.6rem",
                            letterSpacing: "0.12em", textTransform: "uppercase",
                            color: isDark ? "var(--lh-text-dim)" : "rgba(0,0,0,0.4)", border: isDark ? "1px solid var(--lh-cyan-glow)" : "1px solid rgba(0,0,0,0.12)",
                            borderRadius: "100px", padding: "6px 14px", opacity: 0,
                        }}
                    >
                        <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: isDark ? "var(--lh-cyan)" : "#00B8DB", display: "inline-block" }} />
                        Pricing
                    </div>

                    <h2
                        ref={h2Ref}
                        style={{
                            fontFamily: interFont,
                            fontSize: "clamp(1.9rem, 3.5vw, 2.8rem)", fontWeight: 700,
                            color: isDark ? "var(--lh-cyan)" : "#1a1a1a", letterSpacing: "-0.02em", opacity: 0,
                        }}
                    >
                        Choose How Deep<br />You Want to Go
                    </h2>

                    <p
                        ref={pRef}
                        style={{
                            fontSize: "0.87rem", color: isDark ? "var(--lh-text-dim)" : "#666", lineHeight: 1.75,
                            maxWidth: "480px", margin: "10px auto 0", fontStyle: "italic", opacity: 0,
                        }}
                    >
                        Start with Toolbox at $18/month and upgrade to Oracle later if you need automation.
                    </p>
                </div>

                {/* ── Pricing Grid ─────────────────────────────────────────────── */}
                <div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8 md:mt-11"
                >    {plans.map((plan, i) => {
                    const styles = getCardStyles(plan);
                    return (
                    <div
                        key={i}
                        ref={(el) => { cardsRef.current[i] = el; }}
                        style={{
                            borderRadius: "16px", overflow: "hidden",
                            border: `1px solid ${styles.border}`,
                            background: styles.bg,
                            display: "flex", flexDirection: "column",
                            transition: "transform 0.3s, box-shadow 0.3s",
                            opacity: 0,
                            fontFamily: interFont,
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-4px)";
                            e.currentTarget.style.boxShadow = styles.dark
                                ? "0 14px 44px rgba(0,0,0,0.3)"
                                : "0 14px 44px rgba(0,0,0,0.09)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "";
                            e.currentTarget.style.boxShadow = "";
                        }}
                    >
                        {/* Card header */}
                        <div style={{ padding: "24px 24px 0" }}>
                            <div style={{
                                fontFamily: interFont, fontSize: "0.62rem",
                                letterSpacing: "0.12em", textTransform: "uppercase",
                                color: styles.sub, marginBottom: "5px",
                            }}>
                                {plan.tier}
                            </div>
                            <div style={{
                                fontFamily: interFont, fontSize: "1.3rem",
                                fontWeight: 700, color: styles.text, marginBottom: "4px",
                            }}>
                                {plan.name}
                            </div>
                            <div
                                className="p-price"
                                style={{
                                    fontFamily: interFont, fontSize: "2rem",
                                    color: styles.text, lineHeight: 1, marginBottom: "4px",
                                    letterSpacing: "0.01em", opacity: 0,
                                    ...plan.priceStyle,
                                }}
                            >
                                {plan.price}
                            </div>
                            <div style={{
                                fontFamily: interFont, fontSize: "0.58rem",
                                color: styles.sub, letterSpacing: "0.06em", marginBottom: "10px",
                            }}>
                                {plan.freq}
                            </div>
                            <div style={{
                                fontSize: "0.76rem", color: styles.mid, fontStyle: "italic",
                                lineHeight: 1.55, paddingBottom: "16px",
                                borderBottom: `1px solid ${styles.borderB}`,
                            }}>
                                {plan.tagline}
                            </div>
                        </div>

                        {/* Dot texture */}
                        <div style={{
                            height: "80px",
                            backgroundImage: styles.dotPattern
                                ? `radial-gradient(circle, ${styles.dotPattern} 1px, transparent 1px)`
                                : undefined,
                            backgroundSize: "9px 9px",
                            maskImage: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.4) 100%)",
                            WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.4) 100%)",
                        }} />

                        {/* Features */}
                        <div style={{ padding: "16px 24px", flex: 1, display: "flex", flexDirection: "column" }}>
                            <div style={{
                                fontFamily: interFont, fontSize: "0.55rem",
                                textTransform: "uppercase", letterSpacing: "0.1em",
                                color: styles.sub, marginBottom: "10px",
                            }}>
                                Includes:
                            </div>
                            <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "7px", marginBottom: "16px", flex: 1 }}>
                                {plan.features.map((feat, j) => (
                                    <li
                                        key={j}
                                        className="pf-item"
                                        style={{
                                            display: "flex", alignItems: "flex-start", gap: "7px",
                                            fontSize: "0.74rem", color: styles.feat, opacity: 0,
                                            fontFamily: interFont,
                                        }}
                                    >
                                        <span style={{
                                            width: "13px", height: "13px", borderRadius: "50%", flexShrink: 0, marginTop: "1px",
                                            background: plan.featureClass === "white" ? "rgba(255,255,255,0.3)" : "var(--lh-cyan)",
                                            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 14 14' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3 7l3 3 5-5' stroke='white' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
                                            display: "inline-block",
                                        }} />
                                        {feat}
                                    </li>
                                ))}
                            </ul>

                            {/* CTA button */}
                            <button
                                ref={(el) => { ctaBtnsRef.current[i] = el; }}
                                onClick={() => handlePlanClick(plan.tier)}
                                style={{
                                    width: "100%", padding: "14px",
                                    minHeight: "44px", boxSizing: "border-box",
                                    borderRadius: "100px",
                                    fontFamily: interFont,
                                    fontSize: "0.7rem", letterSpacing: "0.05em",
                                    cursor: "pointer", border: "none",
                                    marginTop: "auto", transition: "all 0.2s",
                                    background: styles.ctaBg,
                                    color: styles.ctaColor,
                                    fontWeight: plan.tier === "Oracle Monitor" ? 700 : undefined,
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = plan.tier === "Oracle Monitor" 
                                        ? "rgba(255,255,255,0.88)" 
                                        : "#19d8ff";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = styles.ctaBg;
                                }}
                            >
                                {plan.cta}
                            </button>
                        </div>
                    </div>
                );})}
                </div>

                {/* ── Bottom note ──────────────────────────────────────────────── */}
                <p
                    ref={noteRef}
                    style={{
                        textAlign: "center", marginTop: "20px",
                        fontFamily: interFont, fontSize: "0.8rem", color: isDark ? "var(--lh-text-dim)" : "#666", fontStyle: "italic", opacity: 0,
                    }}
                >
                    Not sure where to start? Start with Toolbox at $18/month and upgrade to Oracle later if you need automation.
                </p>

            </div>
        </div>
    );
}

